'use client';

import { toast } from 'sonner';
import { useSaveRecipe } from '@hooks/recipe/useSaveRecipe';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import SaveIcon from '@repo/ui/src/components/Icons/SaveIcon';
import { classNames } from '@repo/ui/src/utils/classNames';

interface UnsaveButtonProps {
  contentId: string;
  onUnsaved: () => void;
}

const UnsaveButton = ({ contentId, onUnsaved }: UnsaveButtonProps) => {
  const { t } = useTranslation();
  const { trigger, isMutating } = useSaveRecipe({ contentId });

  const handleUnsave = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await trigger();
      onUnsaved();
    } catch {
      toast.error(
        t('savedRecipes.removeError', undefined, { default: 'Failed to remove recipe. Please try again.' }),
      );
    }
  };

  return (
    <button
      className={classNames(
        'absolute top-3 right-3 z-10 w-10 h-10',
        'flex items-center justify-center',
        'bg-white/90 backdrop-blur-sm rounded-full',
        'border border-neutral-200',
        'transition-all duration-200',
        'hover:bg-brand-300 hover:border-brand-300 hover:text-white',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2',
      )}
      onClick={handleUnsave}
      disabled={isMutating}
      data-testid="unsave-button"
      aria-label={t('savedRecipes.remove', undefined, { default: 'Remove from saved recipes' })}
    >
      <SaveIcon fill="currentColor" className="w-5 h-5" />
    </button>
  );
};

export default UnsaveButton;
